import "./Select.scss"

import { ReactNode, useState, useMemo, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { isEqual } from "lodash"
import { AsyncPaginate, withAsyncPaginate, UseAsyncPaginateParams, ComponentProps as WithAsyncPaginateComponentProps } from "react-select-async-paginate"
import ReactSelect, { FormatOptionLabelMeta, Options, MenuPosition, MenuPlacement, GroupBase } from "react-select"
import ReactSelectCreatable, { CreatableProps } from "react-select/creatable"

import { GroupedControl } from "config/constants"
import { IMultipleEntities } from "@/types"
import SelectComponents, { INoOptionsMessageConfigJson } from "./SelectComponents"
import getSelectStyles, { SelectSize, SelectTheme } from "./SelectStyles"
import SelectCreateLabel from "./SelectCreateLabel"
import CustomError from "models/CustomError"
import Highlighter from "components/Highlighter/Highlighter"
import i18n from "intl/i18n"
import { getZIndex } from "utils/modal"
import useSelectOptimization from "hooks/useSelectOptimization"

// react-select 5 no longer exposes these from public entry points
export type FormatOptionLabelContext = "menu" | "value"
export interface Option {
    label: string
    value: string
    data: any
}

// withAsyncPaginate's return type drops the creatable-only props (isValidNewOption,
// createOptionPosition); the cast below is the pattern documented by the library
type AsyncPaginateCreatableType = <OptionType, Group extends GroupBase<OptionType>, Additional, IsMulti extends boolean = false>(
    props: CreatableProps<OptionType, IsMulti, Group>
        & UseAsyncPaginateParams<OptionType, Group, Additional>
        & WithAsyncPaginateComponentProps<OptionType, Group, IsMulti>
) => React.JSX.Element

export const CreatableAsyncPaginate = withAsyncPaginate(ReactSelectCreatable) as AsyncPaginateCreatableType

const rootSelectMenuPortal = document.getElementById("root-select-menu-portal") as HTMLElement

export interface ISelectOption<Value = any> {
    label: string
    value: Value
    group?: string
    isDisabled?: boolean
    hasExternalAction?: boolean
    borderTop?: boolean
}

export interface ISelectProps<OptionValue = any> {
    value?: OptionValue | ISelectOption<OptionValue> | null
    defaultValue?: ISelectOption<OptionValue> | null
    options?: ISelectOption<OptionValue>[]
    groupOptions?(options: ISelectOption<OptionValue>[]): { label: ReactNode, options: ISelectOption<OptionValue>[] }[]
    placeholder?: string
    isInvalid?: boolean
    onChange?(item?: ISelectOption<OptionValue>): void
    getOptions?(search?: string): Promise<IMultipleEntities<any> | null>
    mapLoadedOptions?(options: ISelectOption<OptionValue>[], search: string): ISelectOption<OptionValue>[]
    formatOptionLabel?(value: any): string
    formatOptionValue?(value: any): any
    optionTemplate?(option: ISelectOption<OptionValue>, search: string, labelMeta: FormatOptionLabelMeta<any>): React.JSX.Element | string
    optionTemplateContext?: FormatOptionLabelContext | "all"
    clearOnChange?: boolean
    isCreatable?: boolean
    isClearable?: boolean
    isSearchable?: boolean
    isDisabled?: boolean
    dropdownIndicator?: boolean
    grouped?: GroupedControl
    cacheUniq?: any[]
    additionalIndicators?: React.JSX.Element
    menuWidth?: string
    menuAlign?: "left" | "right"
    menuPlacement?: MenuPlacement
    autoFocus?: boolean
    name?: string
    testIdPrefix?: string
    isInModal?: boolean
    filterOption?: (option: Option, rawInput: string) => boolean
    isUseOptimization?: boolean
    onMenuOpen?(): void
    onMenuClose?(): void
    menuIsOpen?: boolean
    theme?: SelectTheme
    size?: SelectSize
    hideSelectedOptionIcon?: boolean
    hideUnknownValue?: boolean
}

const Select = <OptionValue extends any = string>({
    value: valueProp = null,
    defaultValue: defaultValueProp = null,
    options,
    groupOptions,
    placeholder = i18n.t('common.pleaseSelect'),
    isInvalid = false,
    getOptions,
    mapLoadedOptions,
    formatOptionLabel,
    formatOptionValue,
    optionTemplate,
    optionTemplateContext = "menu",
    onChange = () => { },
    clearOnChange,
    isCreatable,
    isClearable,
    isSearchable = true,
    isDisabled,
    dropdownIndicator = true,
    grouped,
    cacheUniq,
    additionalIndicators,
    menuWidth,
    menuAlign,
    menuPlacement,
    autoFocus,
    name,
    testIdPrefix = "select",
    isInModal,
    filterOption,
    isUseOptimization,
    onMenuOpen,
    onMenuClose,
    menuIsOpen,
    theme,
    size,
    hideSelectedOptionIcon,
    hideUnknownValue,
}: ISelectProps<OptionValue>) => {
    const getCurrentValue = useCallback(() => {
        if (typeof valueProp !== "undefined" && (valueProp !== null || (valueProp === null && defaultValueProp)) && valueProp !== "") {
            const selectedOption = defaultValueProp || options?.find(o => isEqual(o.value, valueProp))
            if (selectedOption) return selectedOption
            return hideUnknownValue ? null : { label: valueProp, value: valueProp }
        }
        return null
    }, [valueProp, defaultValueProp, hideUnknownValue, options])

    const { t } = useTranslation()
    const [value, setValue] = useState<any>(getCurrentValue())
    const [inputValue, setInputValue] = useState("")
    const [currentSearch, setCurrentSearch] = useState("")
    const [loadMoreCall, setLoadMoreCall] = useState<(() => Promise<IMultipleEntities<any> | null>) | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const menuZIndex = useMemo(() => isInModal ? getZIndex() : undefined, [isInModal])
    const [hasMore, setHasMore] = useState(false)
    const { getOptions: getOptimizedOptions, hasMore: hasMoreOptimizedOptions } = useSelectOptimization<OptionValue>({ options, filterOption, groupOptions })
    useEffect(() => {
        setValue(getCurrentValue())
    }, [getCurrentValue])


    async function loadOptions(search: string = "", currentOptions: readonly any[] = []): Promise<any> {
        setErrorMessage(null)

        let foundItems: IMultipleEntities<any> | null = null
        let isFirstPage = true

        try {
            if (currentOptions.length && loadMoreCall && search === currentSearch) {
                foundItems = await loadMoreCall()
                isFirstPage = false
            } else if (getOptions) {
                foundItems = await getOptions(search)
            }
        } catch (e) {
            if (e instanceof CustomError) {
                setErrorMessage(e.message)
            }
            setCurrentSearch(search)
            setLoadMoreCall(null)
            setHasMore(false)
            return {
                options: [],
                hasMore: false
            }
        }

        setLoadMoreCall(foundItems?.loadMore ? () => foundItems!.loadMore! : null)
        setCurrentSearch(search)

        if (!foundItems) {
            setHasMore(false)
            return {
                options: [],
                hasMore: false
            }
        }

        let preparedOptions = foundItems.list.map(item => ({
            label: formatOptionLabel ? formatOptionLabel(item) : item,
            value: formatOptionValue ? formatOptionValue(item) : item
        }))
        if (typeof mapLoadedOptions === "function") {
            preparedOptions = mapLoadedOptions(preparedOptions, search)
        }

        const newHasMore = !!foundItems?.loadMore
        setHasMore(newHasMore)

        return {
            options: groupOptions
                ? groupOptions(preparedOptions || []).filter(group => group.options.length > 0)
                : (search && isCreatable && isFirstPage ? [{
                    label: t('selfPopulating.existingEntries'),
                    options: preparedOptions,
                }] : preparedOptions),
            hasMore: newHasMore,
        }
    }

    function handleChange(option?: any) {
        setErrorMessage(null)
        if (!option?.hasExternalAction) {
            setValue(option || null)
        }
        onChange(option)
        if (clearOnChange) {
            setValue(null)
        }
    }

    function handleAdd(newOptionValue: string) {
        handleChange({
            label: newOptionValue,
            value: newOptionValue
        })
    }

    function isValidNewOption(inpValue: string, selectValue: any, selectOptions: Options<any>): boolean {
        if (!inpValue) {
            return false
        }

        let isValid = true
        for (let option of selectOptions) {
            if (isOptionMatchesInputValue(option, inpValue)) {
                isValid = false
                break
            }
        }

        return isValid
    }

    function isOptionMatchesInputValue(option: any, inpValue: string) {
        if (option.value) {
            return String(option.label).toLowerCase() === String(inpValue).toLowerCase()
        }
        if (option.options) {
            for (let opt of option.options) {
                if (isOptionMatchesInputValue(opt, inpValue)) {
                    return true
                }
            }
        }
        return false
    }

    function getOptionTemplate(option: any, labelMeta: FormatOptionLabelMeta<any>) {
        if (option.__isNew__) {
            return <SelectCreateLabel search={option.value} testId={`${testIdPrefix}-create`} />
        }
        if (optionTemplate && (!optionTemplateContext || optionTemplateContext === "all" || labelMeta.context === optionTemplateContext)) {
            return optionTemplate(option, inputValue, labelMeta)
        }
        const optionLabel = getOptions ? option.label.toString() : (formatOptionLabel ? formatOptionLabel(option.label.toString()) : option.label.toString())
        return (
            <Highlighter text={optionLabel} highlight={inputValue} />
        )
    }

    const components = useMemo(() => SelectComponents({
        dropdownIndicator,
        testIdPrefix,
    }), [dropdownIndicator, testIdPrefix])

    const styles = useMemo(() => {
        return getSelectStyles({
            isInvalid: isInvalid || !!(errorMessage && inputValue),
            isInvalidMenu: !!(errorMessage && inputValue),
            grouped,
            menuWidth,
            menuAlign,
            menuZIndex,
            hasMore: hasMore || hasMoreOptimizedOptions,
            theme,
            size,
            hideSelectedOptionIcon,
        })
    }, [isInvalid, errorMessage, inputValue, grouped, menuWidth, menuAlign, menuZIndex, hasMore, hasMoreOptimizedOptions, theme, size, hideSelectedOptionIcon])

    const noOptionsMessage = () => JSON.stringify({
        isError: !!errorMessage,
        message: errorMessage || t('common.noResultsFound'),
    } as INoOptionsMessageConfigJson)

    /**
     * react-select 5's "auto" placement flips the menu upward too eagerly for a select sitting low
     * in a scrollable container. In-page (non-modal) selects therefore default to "bottom" and rely
     * on react-select scrolling the open menu into view; modal selects (portaled + fixed) keep
     * "auto" so they place correctly against the viewport. Explicit menuPlacement always wins.
     */
    const defaultMenuPlacement: MenuPlacement = isInModal ? "auto" : "bottom"

    const commonProps = {
        classNamePrefix: "select",
        components,
        styles,
        value,
        isSearchable,
        isClearable,
        isDisabled,
        onChange: handleChange,
        onInputChange: setInputValue,
        blurInputOnSelect: true,
        formatOptionLabel: getOptionTemplate,
        noOptionsMessage,
        autoFocus,
        name,
        menuPortalTarget: isInModal ? rootSelectMenuPortal : undefined,
        menuPlacement: menuPlacement || defaultMenuPlacement,
        closeMenuOnScroll: isInModal ? (event) => {
            return !rootSelectMenuPortal.contains(event.target)
        } : false,
        menuPosition: (isInModal ? "fixed" : "absolute") as MenuPosition,
        onMenuOpen,
        onMenuClose,
        menuIsOpen,
    }

    const props = {
        ...commonProps,
        options: groupOptions ? groupOptions(options || []).filter(group => group.options.length > 0) : options,
        placeholder: isDisabled && placeholder === t('common.pleaseSelect') ? "" : placeholder,
        filterOption,
    }
    const optimizedSelectProps = {
        ...commonProps,
        debounceTimeout: 500,
        placeholder: isDisabled && placeholder === t('common.pleaseSelect') ? "" : placeholder,
        filterOption,
        loadOptions: getOptimizedOptions
    }

    const propsAsyncPaginate = {
        ...commonProps,
        placeholder,
        cacheUniqs: [inputValue, ...(cacheUniq || [])],
        debounceTimeout: 500,
        loadOptions,
        onCreateOption: handleAdd,
        createOptionPosition: "first" as const,
        shouldLoadMore: function (scrollHeight: number, clientHeight: number, scrollTop: number) {
            const bottomBorder = scrollHeight - clientHeight - 100
            return bottomBorder < scrollTop
        },
    }

    return (
        <div className={`select ${dropdownIndicator ? 'select--with-dropdown-indicator' : ''}`} data-testid={`${testIdPrefix}-root`}>
            {getOptions ?
                <>
                    {isCreatable &&
                        <CreatableAsyncPaginate
                            {...propsAsyncPaginate}
                            isValidNewOption={isValidNewOption} />
                    }
                    {!isCreatable &&
                        <AsyncPaginate
                            {...propsAsyncPaginate} />
                    }
                </>
                : (isUseOptimization) ?
                    <>
                        {isCreatable &&
                            <CreatableAsyncPaginate
                                {...optimizedSelectProps}
                                createOptionPosition="first"
                                isValidNewOption={isValidNewOption} />
                        }
                        {!isCreatable &&
                            <AsyncPaginate
                                {...optimizedSelectProps}
                            />
                        }
                    </>
                    :
                    <>
                        {isCreatable &&
                            <ReactSelectCreatable
                                {...props}
                                createOptionPosition="first"
                                isValidNewOption={isValidNewOption}
                            />
                        }
                        {!isCreatable &&
                            <ReactSelect
                                {...props}
                            />
                        }
                    </>
            }
            <div className="select__additional-indicators">
                {additionalIndicators}
            </div>
        </div>
    )
}

export default Select
